import {
  actions,
  useCurrentUser,
  type Company,
} from '@kanda-libs/ks-frontend-services';
import type { ExtractedError } from '@kanda-libs/ks-frontend-services/src/types';
import { useAppDispatch } from 'components/App';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectors } from 'store';

interface CurrentCompanyHook {
  company: Company | null;
  error: ExtractedError | undefined;
  id: string | undefined;
  directorsAreVerified: boolean;
  getCompanies: () => Promise<Company[] | undefined>;
  isValidating: boolean;
  isLoading: boolean;
  isSubmitting: boolean;
}

/**
 * Hook to fetch the current company
 */
export default function useCurrentCompany(): CurrentCompanyHook {
  const dispatch = useAppDispatch();
  const { isValidating } = useCurrentUser();

  const isSubmitting = useSelector(selectors.company.getIsSubmitting);
  const hasFetched = useSelector(selectors.company.getHasFetched);
  const error = useSelector(selectors.company.getError);

  const isLoading = useMemo(() => !hasFetched, [hasFetched]);

  const companies = useSelector(selectors.company.getEntitiesAsArray);

  const getCompanies = useCallback(async () => {
    const action = await dispatch(
      actions.getCompanies({
        params: {},
        forceReload: true,
      }),
    );

    return action.payload as Company[];
  }, [dispatch]);

  const company = useMemo(() => {
    if (!companies || companies?.length === 0) return null;
    return companies[0];
  }, [companies]);

  const directorsAreVerified = useMemo(() => {
    if (!company || !company?.users || company?.users?.length === 0) {
      return false;
    }
    if (
      [
        'director_details_submitted',
        'insurance_uploaded',
        'onboarded',
      ].includes(company?.lifecycle || '')
    ) {
      return true;
    }
    const directors = company?.users?.filter((user) => user?.director_info);
    const verified = directors?.filter(
      (director) => director?.director_info?.verification_status === 'verified',
    );
    return verified?.length === directors?.length;
  }, [company]);

  return {
    company,
    error,
    id: company?.id,
    directorsAreVerified,
    getCompanies,
    isValidating,
    isLoading,
    isSubmitting,
  };
}
